/**
 * Iter 338 — Mode borne : tablette sur site, sans connexion.
 * /borne/:terminalId → PIN une fois (jeton 30 j en localStorage) → QR plein écran + horloge + pointages du jour.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Maximize2, Minimize2, WifiOff, Loader2, Delete, LogIn, LogOut, RefreshCw, Lock, CheckCircle2, Volume2, VolumeX } from "lucide-react";

const CONFIRM_MS = 6000;
const EVENTS_POLL_MS = 4000;

const API = process.env.REACT_APP_BACKEND_URL;
const storageKey = (id) => `blitzz.kiosk.${id}`;
const SOUND_KEY = "blitzz.kiosk.sound";

/** Carillon synthétisé (Web Audio) : 2 notes montantes pour l'entrée, descendantes pour la sortie. */
let audioCtx = null;
function getAudio() {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) audioCtx = new Ctx();
  if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
  return audioCtx;
}
export function playChime(kind) {
  const ctx = getAudio();
  if (!ctx || ctx.state !== "running") return false;
  const notes = kind === "in" ? [659.25, 987.77] : [783.99, 523.25];
  notes.forEach((freq, i) => {
    const t0 = ctx.currentTime + i * 0.18;
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.type = "sine"; osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.35, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.55);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0); osc.stop(t0 + 0.6);
  });
  return true;
}

export default function KioskPage() {
  const { terminalId } = useParams();
  const [token, setToken] = useState(() => localStorage.getItem(storageKey(terminalId)) || "");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    document.title = "Blitzz — Borne de pointage";
    axios.get(`${API}/api/tracking/kiosk/${terminalId}/info`).then((r) => setInfo(r.data)).catch(() => setInfo({ error: true }));
  }, [terminalId]);

  const onUnlocked = (t) => { localStorage.setItem(storageKey(terminalId), t); setToken(t); };
  const onExpired = () => { localStorage.removeItem(storageKey(terminalId)); setToken(""); };

  if (info?.error) return <Shell><div className="text-center text-rose-300" data-testid="kiosk-not-found">Poste introuvable ou désactivé.</div></Shell>;
  if (!token) return <PinScreen terminalId={terminalId} info={info} onUnlocked={onUnlocked} />;
  return <KioskBoard terminalId={terminalId} token={token} onExpired={onExpired} />;
}

function Shell({ children }) {
  return <div className="min-h-[100dvh] bg-[#0b0811] text-white flex items-center justify-center p-6 select-none" data-testid="kiosk-page">{children}</div>;
}

function PinScreen({ terminalId, info, onUnlocked }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async (value) => {
    if (value.length < 4 || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await axios.post(`${API}/api/tracking/kiosk/${terminalId}/unlock`, { pin: value });
      onUnlocked(r.data.kiosk_token);
    } catch (e) {
      setError(e.response?.data?.detail || "Déverrouillage impossible");
      setPin("");
    } finally { setBusy(false); }
  }, [terminalId, onUnlocked, busy]);

  const press = (d) => { const v = (pin + d).slice(0, 8); setPin(v); };

  return (
    <Shell>
      <div className="w-full max-w-sm text-center" data-testid="kiosk-pin-screen">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#f7c935] font-black mb-1">Blitzz · Mode borne</div>
        <h1 className="text-2xl font-black mb-1" data-testid="kiosk-pin-title">{info?.name || "Poste de pointage"}</h1>
        <p className="text-xs text-slate-400 mb-6 flex items-center justify-center gap-1"><Lock className="w-3.5 h-3.5" /> Saisissez le PIN de la borne (donné par votre responsable)</p>
        <div className="flex justify-center gap-2 mb-5" data-testid="kiosk-pin-dots">
          {Array.from({ length: Math.max(6, pin.length) }).map((_, i) => <span key={i} className={`w-3.5 h-3.5 rounded-full ${i < pin.length ? "bg-[#f7c935]" : "bg-white/15"}`} />)}
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "del", "0", "ok"].map((k) => (
            <button key={k} type="button" disabled={busy}
              onClick={() => (k === "del" ? setPin((p) => p.slice(0, -1)) : k === "ok" ? submit(pin) : press(k))}
              className={`h-16 rounded-2xl text-2xl font-black active:scale-95 transition-transform ${k === "ok" ? "bg-[#f7c935] text-[#0b0811]" : "bg-white/10 text-white"}`}
              data-testid={`kiosk-key-${k}`}>
              {k === "del" ? <Delete className="w-6 h-6 mx-auto" /> : k === "ok" ? (busy ? <Loader2 className="w-6 h-6 mx-auto animate-spin" /> : "OK") : k}
            </button>
          ))}
        </div>
        {error && <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="kiosk-pin-error">{error}</div>}
      </div>
    </Shell>
  );
}

function KioskBoard({ terminalId, token, onExpired }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());
  const [imgTick, setImgTick] = useState(Date.now());
  const [full, setFull] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const lastQr = useRef(null);
  const wakeLock = useRef(null);
  const sinceRef = useRef(null);
  const seenRef = useRef(new Set());
  const [confirmQueue, setConfirmQueue] = useState([]);
  const confirm = confirmQueue[0] || null;
  const [sound, setSound] = useState(() => localStorage.getItem(SOUND_KEY) !== "off");
  const [audioReady, setAudioReady] = useState(false);

  // Le son n'est autorisé qu'après un geste utilisateur : on déverrouille l'audio au premier appui sur l'écran
  useEffect(() => {
    const unlock = () => { const ctx = getAudio(); if (ctx) ctx.resume().then(() => setAudioReady(ctx.state === "running")).catch(() => {}); };
    unlock();
    window.addEventListener("pointerdown", unlock, { passive: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);
  const toggleSound = () => {
    const next = !sound;
    setSound(next); localStorage.setItem(SOUND_KEY, next ? "on" : "off");
    if (next) { const ok = playChime("in"); setAudioReady(ok); }
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/tracking/kiosk/${terminalId}/state`, { params: { token } });
      setState(r.data); setError(null);
      if (!sinceRef.current) sinceRef.current = r.data.server_time; // horloge serveur comme référence
      if (lastQr.current && lastQr.current !== r.data.qr_token) setImgTick(Date.now());
      lastQr.current = r.data.qr_token;
    } catch (e) {
      if (e.response?.status === 401) return onExpired();
      setError(e.response?.data?.detail || "Serveur injoignable");
    }
  }, [terminalId, token, onExpired]);

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  // Retour visuel : nouveaux pointages sur ce poste (scan synchronisé) → confirmation verte quelques secondes
  const pollEvents = useCallback(async () => {
    if (!sinceRef.current) return;
    try {
      const r = await axios.get(`${API}/api/tracking/kiosk/${terminalId}/events`, { params: { token, since: sinceRef.current } });
      sinceRef.current = r.data.server_time;
      const fresh = (r.data.events || []).filter((e) => !seenRef.current.has(e.key));
      if (fresh.length) {
        fresh.forEach((e) => seenRef.current.add(e.key));
        setConfirmQueue((q) => [...q, ...fresh]);
        load();
      }
    } catch (e) { if (e.response?.status === 401) onExpired(); }
  }, [terminalId, token, load, onExpired]);
  useEffect(() => { const id = setInterval(pollEvents, EVENTS_POLL_MS); return () => clearInterval(id); }, [pollEvents]);
  useEffect(() => {
    if (!confirm) return undefined;
    if (sound) setAudioReady(playChime(confirm.kind));
    const id = setTimeout(() => setConfirmQueue((q) => q.slice(1)), CONFIRM_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirm]);
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    const onFs = () => setFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); document.removeEventListener("fullscreenchange", onFs); };
  }, []);
  // Écran maintenu allumé (Wake Lock API) — ré-acquis quand l'onglet redevient visible
  useEffect(() => {
    const acquire = async () => { try { if (navigator.wakeLock && document.visibilityState === "visible") wakeLock.current = await navigator.wakeLock.request("screen"); } catch { /* non supporté */ } };
    acquire();
    document.addEventListener("visibilitychange", acquire);
    return () => { document.removeEventListener("visibilitychange", acquire); wakeLock.current?.release?.(); };
  }, []);
  // Rafraîchit l'image du QR à la rotation exacte (+2 s de marge) et au minimum chaque minute
  useEffect(() => {
    if (!state?.rotates_at) return undefined;
    const ms = Math.max(2000, new Date(state.rotates_at).getTime() - Date.now() + 2000);
    const id = setTimeout(() => { setImgTick(Date.now()); load(); }, Math.min(ms, 60000));
    return () => clearTimeout(id);
  }, [state?.rotates_at, imgTick, load]);

  const countdown = useMemo(() => {
    if (!state?.rotates_at) return "--:--";
    const s = Math.max(0, Math.floor((new Date(state.rotates_at).getTime() - now.getTime()) / 1000));
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  }, [state?.rotates_at, now]);

  const toggleFull = () => { if (document.fullscreenElement) document.exitFullscreen?.(); else document.documentElement.requestFullscreen?.(); };
  const rows = useMemo(() => todayEvents(state?.today_punches || [], terminalId), [state?.today_punches, terminalId]);
  const soundHint = sound && !audioReady;

  return (
    <div className="min-h-[100dvh] bg-[#0b0811] text-white flex flex-col select-none" data-testid="kiosk-board">
      {confirm && <ScanConfirmation event={confirm} onDismiss={() => setConfirmQueue((q) => q.slice(1))} />}
      <header className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#f7c935] font-black">Blitzz · Borne de pointage</div>
          <div className="text-lg font-black leading-tight" data-testid="kiosk-terminal-name">{state?.name || "…"}{state?.site ? <span className="text-slate-400 font-medium text-sm"> · {state.site}</span> : null}</div>
        </div>
        <div className="flex items-center gap-3">
          {!online && <span className="text-[11px] px-2 py-1 rounded-full bg-amber-500/15 text-amber-300 font-bold flex items-center gap-1" data-testid="kiosk-offline"><WifiOff className="w-3.5 h-3.5" /> Hors ligne</span>}
          <button onClick={toggleSound} className={`p-2 rounded-xl ${sound ? "bg-white/10" : "bg-white/5 text-slate-500"}`} title={sound ? (audioReady ? "Son activé" : "Son activé — touchez l'écran une fois pour autoriser l'audio") : "Son coupé"} data-testid="kiosk-sound-btn" data-sound={sound ? "on" : "off"} data-audio-ready={audioReady ? "1" : "0"}>
            {sound ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          <div className="text-4xl font-black tabular-nums" data-testid="kiosk-clock">{now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
          <button onClick={toggleFull} className="p-2 rounded-xl bg-white/10" title="Plein écran" data-testid="kiosk-fullscreen-btn">{full ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}</button>
        </div>
      </header>

      <main className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 p-6 items-center">
        <section className="flex flex-col items-center justify-center gap-4">
          <div className="bg-white rounded-3xl p-5 shadow-[0_0_80px_rgba(247,201,53,0.15)]">
            <img key={imgTick} src={`${API}/api/tracking/qr/${terminalId}/image?v=${imgTick}`} alt="QR de pointage" className="w-[min(70vw,440px)] h-[min(70vw,440px)]" data-testid="kiosk-qr-image" />
          </div>
          <div className="text-center">
            <div className="text-base font-bold">Scannez avec l'app <span className="text-[#f7c935]">Blitzz Salarié</span> pour pointer</div>
            <div className="text-xs text-slate-400 mt-1 flex items-center justify-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Nouveau code dans <span className="font-black tabular-nums text-white" data-testid="kiosk-countdown">{countdown}</span></div>
            {soundHint && <div className="text-[11px] text-amber-300 mt-2 flex items-center justify-center gap-1" data-testid="kiosk-sound-hint"><Volume2 className="w-3.5 h-3.5" /> Touchez l'écran une fois pour activer le son de confirmation</div>}
          </div>
          {error && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="kiosk-error">{error}</div>}
        </section>

        <section className="bg-[#14101c] border border-white/10 rounded-3xl p-5 max-h-[70dvh] flex flex-col" data-testid="kiosk-today">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold">Pointages du jour sur ce poste</h2>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-slate-300" data-testid="kiosk-today-count">{rows.length}</span>
          </div>
          {rows.length === 0 ? (
            <p className="text-sm text-slate-500 italic" data-testid="kiosk-today-empty">Aucun pointage aujourd'hui pour l'instant.</p>
          ) : (
            <ul className="space-y-2 overflow-auto pr-1">
              {rows.map((r) => (
                <li key={r.key} className="flex items-center gap-3 bg-[#0b0811] border border-white/5 rounded-xl px-3 py-2" data-testid={`kiosk-event-${r.key}`}>
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center ${r.kind === "in" ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>{r.kind === "in" ? <LogIn className="w-4 h-4" /> : <LogOut className="w-4 h-4" />}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{r.name}</div>
                    <div className="text-[11px] text-slate-400">{r.kind === "in" ? "Entrée" : "Sortie"}{r.qr ? " · QR" : ""}</div>
                  </div>
                  <div className="text-xl font-black tabular-nums">{new Date(r.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

/** Confirmation plein écran après un scan : entrée (vert) ou sortie (bleu), fermée automatiquement. */
function ScanConfirmation({ event, onDismiss }) {
  const isIn = event.kind === "in";
  const first = (event.name || "").split(" ")[0] || event.name;
  const time = event.at ? new Date(event.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div onClick={onDismiss} className={`fixed inset-0 z-[90] flex flex-col items-center justify-center text-center p-8 animate-in fade-in zoom-in-95 duration-300 ${isIn ? "bg-emerald-600" : "bg-sky-700"}`} data-testid={`kiosk-confirm-${event.kind}`}>
      <div className="w-32 h-32 rounded-full bg-white/20 flex items-center justify-center mb-6">
        {isIn ? <CheckCircle2 className="w-20 h-20 text-white" /> : <LogOut className="w-20 h-20 text-white" />}
      </div>
      <div className="text-2xl font-bold text-white/80 uppercase tracking-[0.2em]">{isIn ? "Entrée pointée" : "Sortie pointée"}</div>
      <div className="text-5xl sm:text-6xl font-black text-white mt-3 break-words max-w-[90vw]" data-testid="kiosk-confirm-name">{isIn ? `Bonjour ${first}` : `À bientôt ${first}`}</div>
      <div className="text-3xl font-bold text-white/90 mt-4 tabular-nums" data-testid="kiosk-confirm-time">{time}{event.qr ? " · QR vérifié" : ""}</div>
      <div className="text-sm text-white/60 mt-8">{isIn ? "Bonne journée !" : "Pointage enregistré, bonne soirée !"}</div>
      <div className="absolute bottom-0 left-0 h-1.5 bg-white/70 animate-[shrink_6s_linear_forwards]" style={{ width: "100%" }} />
      <style>{`@keyframes shrink { from { width: 100% } to { width: 0 } }`}</style>
    </div>
  );
}

/** Transforme les pointages en événements entrée/sortie effectués sur ce poste, du plus récent au plus ancien. */
function todayEvents(punches, terminalId) {
  const out = [];
  const today = new Date().toDateString();
  punches.forEach((p) => {
    if (p.start_terminal_id === terminalId && p.start_at && new Date(p.start_at).toDateString() === today) out.push({ key: `${p.punch_id}-in`, kind: "in", name: p.user_name || "—", at: p.start_at, qr: !!p.qr_verified });
    if (p.stop_terminal_id === terminalId && p.stop_at && new Date(p.stop_at).toDateString() === today) out.push({ key: `${p.punch_id}-out`, kind: "out", name: p.user_name || "—", at: p.stop_at, qr: !!p.stop_qr_verified });
  });
  return out.sort((a, b) => new Date(b.at) - new Date(a.at));
}
